// Dashboard — Phase 1 lands the greeting + tool tiles. KPI strip, recent
// projects, activity feed, and quick actions land once the tools have data
// to feed them (Phase 3+).
import { Link } from "react-router";
import {
  Wrench,
  Target,
  Building2,
  CalendarClock,
  LayoutDashboard,
  Scissors,
  type LucideIcon,
} from "lucide-react";
import { TOOL_REGISTRY } from "../../../shared/tools/registry";
import { useAuth } from "../auth/useAuth";

const ICONS: Record<string, LucideIcon> = {
  Wrench,
  Target,
  Building2,
  CalendarClock,
  LayoutDashboard,
  Scissors,
};

function ToolTile({
  to,
  icon: Icon,
  name,
  description,
  status,
}: {
  to: string;
  icon: LucideIcon;
  name: string;
  description: string;
  status: "active" | "beta" | "coming-soon";
}) {
  const disabled = status === "coming-soon";
  const content = (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Icon size={20} color="var(--bsp-blue)" />
        <h3>{name}</h3>
        {status === "beta" && (
          <span className="bsp-eyebrow" style={{ marginLeft: "auto" }}>
            Beta
          </span>
        )}
        {disabled && (
          <span className="bsp-eyebrow" style={{ marginLeft: "auto", color: "var(--bsp-fg-muted)" }}>
            Coming soon
          </span>
        )}
      </div>
      <p>{description}</p>
    </>
  );
  if (disabled) {
    return (
      <div className="bsp-tool-tile" style={{ opacity: 0.65, cursor: "not-allowed" }}>
        {content}
      </div>
    );
  }
  return (
    <Link to={to} className="bsp-tool-tile">
      {content}
    </Link>
  );
}

export function Dashboard() {
  const { user, isAdmin } = useAuth();
  const granted = new Set(user?.toolPermissions ?? []);
  // Match server-side authorisation: admins see every registered tool;
  // everyone else sees only the tools they've been granted. Tools the user
  // can't access don't render here so we don't surface clickable tiles that
  // would 403 on the server (see server/routes/storage.ts userHasToolAccess).
  const visibleTools = TOOL_REGISTRY.filter(
    (t) => isAdmin || granted.has(t.id),
  );

  const greeting =
    new Date().getHours() < 12
      ? "Good morning"
      : new Date().getHours() < 18
        ? "Good afternoon"
        : "Good evening";
  const firstName = (user?.displayName ?? "").split(" ")[0] ?? "";

  return (
    <>
      <div style={{ marginBottom: 28 }}>
        <div className="bsp-eyebrow">{greeting}</div>
        <h1 className="bsp-page-title" style={{ marginTop: 4 }}>
          {firstName ? `${firstName}, welcome back.` : "Welcome back."}
        </h1>
      </div>

      <section>
        <div className="bsp-label" style={{ marginBottom: 12 }}>Tools</div>
        {visibleTools.length === 0 ? (
          <p style={{ color: "var(--bsp-fg-muted)" }}>
            No tools available yet. Ask an administrator to grant access.
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 16,
            }}
          >
            {visibleTools.map((tool) => (
              <ToolTile
                key={tool.id}
                to={tool.route}
                icon={ICONS[tool.icon] ?? Wrench}
                name={tool.name}
                description={tool.description}
                status={tool.status}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
}
